/**
 * Il tasto "Aggiorna da Fatture in Cloud".
 *
 * Gli incassi si segnano su Fatture in Cloud e Pixel li rilegge ogni notte:
 * questo tasto fa lo stesso giro subito, sulle fatture attive (o, in
 * Passivita', sulle passive comprese quelle da registrare). Lo vede
 * solo chi la funzione accetta (admin e amministrazione); per gli altri non
 * compare, invece di comparire e poi rispondere "non autorizzato".
 */

import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Button } from './ui/button';
import { useStore } from '../store/useStore';
import { cn } from '../lib/cn';
import {
  aggiornaDaFattureInCloud,
  aggiornaPassiveDaFattureInCloud,
  leggiUltimoAggiornamento,
  puoAggiornareDaFic,
  type EsitoAggiornamentoFic,
} from '../lib/ficSync';

interface Props {
  ruolo: string | null | undefined;
  /** Chiamata dopo un aggiornamento riuscito, per rileggere i dati mostrati. */
  onAggiornato?: () => void;
  className?: string;
  /** Versione piccola, per stare dentro un widget della dashboard. */
  compatto?: boolean;
  /** Cosa rileggere: le fatture attive (predefinito) o le passive. */
  cosa?: 'attive' | 'passive';
}

function riassunto(esito: EsitoAggiornamentoFic): string {
  const stornati = esito.stornati ? `, ${esito.stornati} stornati` : '';
  return `${esito.incassati} incassati, ${esito.daIncassare} da incassare${stornati}`;
}

export function AggiornaDaFic({ ruolo, onAggiornato, className, compatto = false, cosa = 'attive' }: Props) {
  const addNotification = useStore((s) => s.addNotification);
  const abilitato = puoAggiornareDaFic(ruolo);
  const [inCorso, setInCorso] = useState(false);
  const [ultimo, setUltimo] = useState<string | null>(null);

  useEffect(() => {
    if (!abilitato) return;
    let attivo = true;
    void leggiUltimoAggiornamento(cosa === 'passive' ? 'fatture_passive' : 'fatture_attive').then((quando) => {
      if (attivo) setUltimo(quando);
    });
    return () => {
      attivo = false;
    };
  }, [abilitato, cosa]);

  if (!abilitato) return null;

  const aggiorna = async () => {
    setInCorso(true);
    try {
      if (cosa === 'passive') {
        const esito = await aggiornaPassiveDaFattureInCloud();
        const avvisi = esito.avvisi.length ? ` Attenzione, saltato: ${esito.avvisi.join('; ')}` : '';
        addNotification({
          type: esito.avvisi.length ? 'warning' : 'success',
          title: 'Aggiornato da Fatture in Cloud',
          message: `${esito.registrate} registrate, ${esito.daRegistrare} da registrare.${avvisi}`,
        });
      } else {
        const esito = await aggiornaDaFattureInCloud();
        addNotification({ type: 'success', title: 'Aggiornato da Fatture in Cloud', message: riassunto(esito) });
      }
      setUltimo(new Date().toISOString());
      onAggiornato?.();
    } catch (errore) {
      addNotification({
        type: 'error',
        title: 'Aggiornamento non riuscito',
        message: errore instanceof Error ? errore.message : 'Riprova tra qualche minuto',
      });
    } finally {
      setInCorso(false);
    }
  };

  if (compatto) {
    return (
      <button
        type="button"
        disabled={inCorso}
        onClick={aggiorna}
        title="Rilegge fatture e incassi da Fatture in Cloud"
        className={cn('flex min-w-0 items-center gap-1 whitespace-nowrap text-[11px] font-medium opacity-75 transition hover:opacity-100 disabled:opacity-50', className)}
      >
        <RefreshCw className={cn('h-3 w-3 flex-shrink-0', inCorso && 'animate-spin')} />
        <span className="truncate">
          {inCorso ? 'Aggiornamento…' : ultimo ? `Aggiornato ${format(new Date(ultimo), "d MMM HH:mm", { locale: it })}` : 'Aggiorna da Fatture in Cloud'}
        </span>
      </button>
    );
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Button
        variant="outline"
        className="h-10"
        disabled={inCorso}
        onClick={aggiorna}
        title="Rilegge fatture e incassi da Fatture in Cloud"
      >
        <RefreshCw className={cn('w-4 h-4 mr-2', inCorso && 'animate-spin')} />
        {inCorso ? 'Aggiornamento…' : 'Aggiorna da Fatture in Cloud'}
      </Button>
      {ultimo && (
        <span className="hidden text-xs text-neutral-500 lg:inline">
          Ultimo: {format(new Date(ultimo), "d MMM 'alle' HH:mm", { locale: it })}
        </span>
      )}
    </div>
  );
}
